import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styles from './MemberDetails.module.css';
import { toast } from 'react-toastify';

import {
  sendInvitation,
  deleteMember,
  unlinkSecondaryContact,
  addSecondaryContact,
} from 'api/userApi';
import routes from 'routes';
import { memberListRouteForRole } from 'utils/memberLists';
import {
  getUserPermissionLevel,
  getUserEmail,
} from 'services/auth/tokenService';
import LabelText from 'components/common/LabelText';
import CustomButton, {
  defaultButtonStyle,
} from 'components/common/CustomButton';
import PinProtectedButton from 'components/common/PinProtected/PinProtectedButton';

// Import the new modal components
import MemberFormModal from '../MemberFormModal/MemberFormModal';
import SecondaryContactModal from '../SecondaryContactModal/SecondaryContactModal';
import AthleteFormModal from '../AthleteFormModal/AthleteFormModal';
import LinkAthleteToClubDialog from '../LinkAthleteToClubDialog';
import CustomConfirmModal from 'components/common/CustomConfirmModal';
import RosterRemovePreviewModal from 'components/admin/RosterRemovePreviewModal';
import AthleteProfiles from '../AthleteProfiles/AthleteProfiles';
import useClubTeams from 'hooks/useClubTeams';
import { UserPermissionLevel } from 'constants/auth';
import { clubForId, invitationAccessText } from 'utils/clubFeatures';

// Page type context for the remove-preview summary ("guest member/athlete" etc.).
const roleTypeLabel = (role) =>
  role === 'Guest' ? 'guest' : role === 'Visitor' ? 'visitor' : '';

const MemberDetails = ({
  member,
  clubId,
  secondaryContact,
  athletes = [],
  otherClubAthletes = [],
  viewingAs = 'Primary',
  onRefresh,
}) => {
  const navigate = useNavigate();

  // The club this page is for, from the club list already in memory (no request): the line under Send Invitation
  // names the Club Connect app only for a club whose invitations offer it.
  const club = useSelector((state) => clubForId(state.user, clubId));

  // The club's teams (a school's houses or factions), loaded once here and passed to the athlete card and the two
  // athlete dialogs. One request, answered from the server's cache, and none at all for a club without teams.
  const clubTeams = useClubTeams(clubId);

  const [inviting, setInviting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Modal states
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showSecondaryModal, setShowSecondaryModal] = useState(false);
  const [showAthleteModal, setShowAthleteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showLinkAthleteDialog, setShowLinkAthleteDialog] = useState(false);
  const [editingAthlete, setEditingAthlete] = useState(null);
  const [linkAthleteTarget, setLinkAthleteTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [inviteTarget, setInviteTarget] = useState(null);
  const [isResendInvite, setIsResendInvite] = useState(false);

  // Athlete/member removal goes through the shared change-preview planner — same cascade rules as the bulk lists
  // (e.g. removing a guest's only athlete also removes the now-childless guest member). { seed, title, typeLabel }
  // or null. Secondary-contact actions keep the simple confirm modal below.
  const [rosterRemove, setRosterRemove] = useState(null);

  // Role based permissions
  // Primary Contact:
  // - Can edit their own profile
  // - Can add, edit, delete secondary contacts
  // - Can view athlete details
  // - Cannot Delete their own profile
  // - Cannot Add, edit or delete athlete profiles

  // Admin/Owner:
  // - No restrictions

  // Seconmdary COntact:
  // - Can view member details
  // - Cannot add, edit  or delete any data

  const currentUserEmail = getUserEmail();
  const userPermissionLevel = getUserPermissionLevel();

  // Make email comparisons case-insensitive - fixes issues with Mobile browser auto capitalizing the First Letter
  const isOwnProfile =
    currentUserEmail?.toLowerCase() === member.email?.toLowerCase();

  const canEditMember =
    isOwnProfile || userPermissionLevel >= UserPermissionLevel.Official;
  const canDeleteMember = userPermissionLevel >= UserPermissionLevel.Official;
  // "Primary contact" is a FAMILY role: the family's primary may manage their secondary regardless of club
  // membership type (Full Member or Guest), provided they are Active in the club; plus club staff (>= Official).
  // See docs/secondary-contact-decisions.md.
  const canEditSecondaryContact =
    (isOwnProfile && member?.participationStatus === 'Active') ||
    userPermissionLevel >= UserPermissionLevel.Official;
  // Unlink is wider than edit/add: per role-and-deletion-design §5.3 the
  // backend allows the family's primary contact at any active level (including
  // Guest) to unlink their own secondary contact, plus club Admin/Owner.
  const canUnlinkSecondaryContact =
    isOwnProfile || userPermissionLevel >= UserPermissionLevel.Admin;
  // "Remove from club" for the secondary is a club-scoped member removal — Admin/Owner only (the primary may
  // manage the family relationship, but not remove someone from the club). See secondary-contact-family-ui-plan §5.
  const canRemoveSecondaryFromClub =
    userPermissionLevel >= UserPermissionLevel.Admin;
  const canAddAthlete = userPermissionLevel >= UserPermissionLevel.Official; // Only admins can add athletes for primary members
  const canEditDeleteAthlete =
    userPermissionLevel >= UserPermissionLevel.Official; // Only admins can edit/delete athletes for primary members

  // Family-view flags. viewingAs (server) says which contact was opened so we can highlight it. isClubMember
  // (server) is false for a family member not in THIS club — shown as a not-in-club row rather than with club
  // fields. See docs/secondary-contact-family-ui-plan.html §4.
  const openedSecondary = viewingAs === 'Secondary';
  const openedPrimary = !openedSecondary;
  const memberInClub = member?.isClubMember !== false;
  const secondaryInClub = secondaryContact?.isClubMember !== false;

  const handleDeleteMemberWithPin = async (memberData) => {
    setRosterRemove({
      seed: { userIds: [memberData.userID] },
      title: 'Remove Member',
      typeLabel: roleTypeLabel(memberData?.role || member?.role),
    });
  };

  // Admin-only, PIN-gated: removing a secondary contact from the club is a club-scoped member removal.
  const handleRemoveSecondaryFromClubWithPin = async (contactData, pin) => {
    setDeleteTarget({ type: 'remove-secondary-from-club', item: contactData });
    setShowDeleteModal(true);
  };

  // Admin-only, PIN-gated: add the family's secondary (linked via the platform, but not a member of this club) to
  // the club. Reuses add-secondary, which creates the membership for the family's existing secondary.
  const handleAddSecondaryToClubWithPin = async (contactData) => {
    try {
      await addSecondaryContact(clubId, member.userID, {
        email: contactData.email,
        firstName: contactData.firstName,
        lastName: contactData.lastName,
        createUserIfNotExists: false,
      });
      toast.success('Secondary contact added to the club');
      onRefresh?.();
    } catch (error) {
      const msg =
        error?.response?.data ||
        error?.message ||
        'Failed to add the secondary contact to the club';
      toast.error(
        typeof msg === 'string'
          ? msg
          : 'Failed to add the secondary contact to the club'
      );
    }
  };

  const handleInviteClick = (contact, isResend = false) => {
    // Check if member status is Active before showing confirmation modal
    if (contact.participationStatus !== 'Active') {
      toast.error(
        'Cannot send invitation to Inactive Member: status must be Active to receive invitations'
      );
      return; // Don't show the modal at all
    }

    setInviteTarget(contact);
    setIsResendInvite(isResend);
    setShowInviteModal(true);
  };

  const getDisplayName = (contact) => {
    const hasFirstName = contact?.firstName && contact.firstName.trim() !== '';
    const hasLastName = contact?.lastName && contact.lastName.trim() !== '';

    if (hasFirstName || hasLastName) {
      return `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
    }

    return contact?.email || 'Unknown Contact';
  };

  const confirmInvitation = async () => {
    if (!inviteTarget) return;

    setInviting(true);
    try {
      await sendInvitation(clubId, inviteTarget.email);
      toast.success(
        isResendInvite
          ? 'Invitation resent successfully'
          : 'Invitation sent successfully'
      );
      setShowInviteModal(false);
      setInviteTarget(null);
      // Refresh the member data to show updated invitation status
      onRefresh?.();
    } catch (err) {
      console.log('[MemberDetails] Member invitation error: ', err);
      // Server returns error message directly as a string in response.data
      const errorMessage =
        err?.response?.data || err?.message || 'Failed to send invitation';
      toast.error(errorMessage);
    } finally {
      setInviting(false);
    }
  };

  const handleDeleteClick = (type, item) => {
    if (type === 'athlete') {
      setRosterRemove({
        seed: { athleteIds: [item.athleteID] },
        title: 'Remove Athlete',
        typeLabel: roleTypeLabel(member?.role),
      });
      return;
    }
    setDeleteTarget({ type, item });
    setShowDeleteModal(true);
  };

  // Removal applied via the planner. If the member we're viewing was actually removed (directly or by the
  // cascade), leave the page — it would 404; otherwise refresh in place.
  const handleRosterRemoveApplied = (applyResult) => {
    const memberRemoved = (applyResult?.results || []).some(
      (r) => r.id === member?.userID && r.success
    );
    if (memberRemoved) {
      navigate(memberListRouteForRole(member?.role));
    } else {
      onRefresh?.();
    }
  };

  // After a secondary-contact action, decide how to refresh. `member` is ALWAYS the family's primary
  // (the anchor), so when the page was opened via the SECONDARY (viewingAs === 'Secondary') their URL
  // may no longer resolve — removed from the club it 404s (→ bounce to the members list), and after a
  // delete-from-family it's no longer this family's secondary. Re-anchor on the primary instead of
  // re-fetching the stale secondary URL. When opened via the primary, refresh in place.
  const refreshAfterSecondaryChange = () => {
    if (openedSecondary && member?.email) {
      navigate(routes.admin.members.showMember.url(clubId, member.email));
    } else {
      onRefresh?.();
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      if (deleteTarget.type === 'delete-secondary-from-family') {
        // Delete from family: sever the platform secondary-contact relationship (ALL clubs). The person KEEPS
        // their club membership (relabelled NoAthletes by the server) so an admin can decide to remove them from
        // the club separately. See docs/secondary-contact-family-ui-plan.html §5.
        await unlinkSecondaryContact(clubId, member.userID);
        toast.success('Secondary contact removed from the family');
        refreshAfterSecondaryChange();
      } else if (deleteTarget.type === 'remove-secondary-from-club') {
        // Remove from club: drop the secondary's membership in THIS club only. The platform family relationship
        // is kept, so they remain the family's secondary (shown "not in this club") and can be re-added.
        await deleteMember(clubId, deleteTarget.item.email);
        toast.success('Secondary contact removed from the club');
        refreshAfterSecondaryChange();
      }

      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error('[MemberDetails] Remove error:', error);
      const errorMessage =
        error?.response?.data ||
        error?.message ||
        `Failed to remove ${deleteTarget.type}`;
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleEditAthlete = (athlete) => {
    setEditingAthlete(athlete);
    setShowAthleteModal(true);
  };

  const handleAddAthlete = () => {
    setEditingAthlete(null);
    setShowAthleteModal(true);
  };

  const handleLinkAthlete = (athlete) => {
    setLinkAthleteTarget(athlete);
    setShowLinkAthleteDialog(true);
  };

  const handleAddSecondaryContact = () => {
    // Check if there are any athletes before allowing secondary contact
    if (!athletes || athletes.length === 0) {
      toast.error(
        'Cannot add secondary contact: At least one athlete profile must be added first'
      );
      return;
    }
    setShowSecondaryModal(true);
  };

  const renderInviteButton = (contact, contactType) => {
    if (!contact || contact.invitationStatus === 'Accepted') {
      return null;
    }

    const isPending = contact.invitationStatus === 'Pending';

    return (
      <CustomButton
        style={{
          ...defaultButtonStyle,
          marginBottom: 0,
          height: 'fit-content',
          padding: '4px 16px',
          fontSize: '13px',
          backgroundColor: isPending ? '#f59e0b' : '#cf2c47',
        }}
        onClick={() => handleInviteClick(contact, isPending)}
        title={isPending ? 'Resend invitation email' : 'Send invitation email'}
      >
        {isPending ? 'Resend Invite' : 'Invite'}
      </CustomButton>
    );
  };

  return (
    <div className={styles.container}>
      {/* Member Details Card */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3 className={styles.cardTitle}>
            Member Details
            {openedPrimary && (
              <span style={{ marginLeft: 8, fontSize: 12, color: '#2563eb', fontWeight: 600 }}>
                ← you opened this
              </span>
            )}
          </h3>
          {canEditMember && memberInClub && (
            <div className={styles.actionButtons}>
              <CustomButton
                style={{ ...defaultButtonStyle, marginBottom: 0, height: 'fit-content', padding: '4px 12px', fontSize: '13px', backgroundColor: '#3b82f6' }}
                onClick={() => setShowMemberModal(true)}
                title="Edit this member's details"
              >
                Edit
              </CustomButton>
              {/* PIN REQUIRED - Remove member from club */}
              {canDeleteMember && (
                <PinProtectedButton
                  clubId={clubId}
                  actionType="delete-member"
                  onAction={handleDeleteMemberWithPin}
                  description="The Admin PIN is required to remove a member from the club. This removes the member from this club only; their portal account and any membership in other clubs are preserved."
                  actionData={member}
                >
                  <button
                    style={{ marginBottom: 0, height: 'fit-content', padding: '4px 12px', fontSize: '13px', backgroundColor: '#f39c12', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    title="Remove this member from the club"
                  >
                    Remove from club
                  </button>
                </PinProtectedButton>
              )}
            </div>
          )}
        </div>

        <div className={styles.cardContent}>
          <div className={styles.infoGrid}>
            <LabelText
              label="Member Name"
              text={`${member.firstName} ${member.lastName}`}
            />
            <LabelText label="Member Email" text={member.email} />
          </div>

          {memberInClub ? (
            <div className={styles.statusRow}>
              <LabelText label="Permissions" text={member.role} />
              <LabelText label="Club Status" text={member.participationStatus} />
              <>
                <LabelText
                  label="Portal Access"
                  text={member.invitationStatus}
                />
                {member.invitationStatus !== 'Accepted' && (
                  <div className={styles.inviteButtonWrapper}>
                    {renderInviteButton(member, 'member')}
                  </div>
                )}
              </>
            </div>
          ) : (
            <div className={styles.statusRow}>
              <LabelText
                label="Club Status"
                text="Not a member of this club — primary contact for the athletes below. Add them from the club member list to manage this family here."
              />
              <LabelText label="Portal Access" text={member.invitationStatus} />
            </div>
          )}
        </div>
      </div>

      {/* Secondary Contact Card */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3 className={styles.cardTitle}>
            Secondary Contact
            {openedSecondary && (
              <span style={{ marginLeft: 8, fontSize: 12, color: '#6d28d9', fontWeight: 600 }}>
                ← you opened this
              </span>
            )}
          </h3>
          {(canEditSecondaryContact ||
            canUnlinkSecondaryContact ||
            canRemoveSecondaryFromClub) && (
            <div className={styles.actionButtons}>
              {secondaryContact ? (
                <>
                  {/* Edit only when they're a member of this club (no membership ⇒ no role/status to edit). */}
                  {canEditSecondaryContact && secondaryInClub && (
                    <CustomButton
                      style={{ ...defaultButtonStyle, marginBottom: 0, height: 'fit-content', padding: '4px 12px', fontSize: '13px', backgroundColor: '#3b82f6' }}
                      onClick={() => setShowSecondaryModal(true)}
                      title="Edit this secondary contact's details"
                    >
                      Edit
                    </CustomButton>
                  )}
                  {/* Add to club: create the club membership for a secondary who is the family's secondary
                      (platform link intact) but is not a member of this club — whether removed earlier or never a
                      member here (e.g. the family was added via another club). Admin-only, PIN-gated. Requires the
                      primary to be in the club (a secondary's membership depends on the primary's). */}
                  {canRemoveSecondaryFromClub && !secondaryInClub && memberInClub && (
                    <PinProtectedButton
                      clubId={clubId}
                      actionType="add-member"
                      onAction={handleAddSecondaryToClubWithPin}
                      description="The Admin PIN is required to add this secondary contact to the club. They are already the family's secondary contact; this creates their club membership (Member)."
                      actionData={secondaryContact}
                    >
                      <button
                        style={{ marginBottom: 0, height: 'fit-content', padding: '4px 12px', fontSize: '13px', backgroundColor: '#27ae60', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        title="Add this secondary contact to the club"
                      >
                        Add to club
                      </button>
                    </PinProtectedButton>
                  )}
                  {/* Remove from club: club-scoped, Admin-only, PIN-gated (mirrors member removal). */}
                  {canRemoveSecondaryFromClub && secondaryInClub && (
                    <PinProtectedButton
                      clubId={clubId}
                      actionType="delete-member"
                      onAction={handleRemoveSecondaryFromClubWithPin}
                      description="The Admin PIN is required to remove a secondary contact from this club. This removes them from THIS club only — they remain the family's secondary contact (re-addable) and keep any membership in other clubs."
                      actionData={secondaryContact}
                    >
                      <button
                        style={{ marginBottom: 0, height: 'fit-content', padding: '4px 12px', fontSize: '13px', backgroundColor: '#f39c12', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        title="Remove from THIS club only — keeps them as the family's secondary contact, re-addable later"
                      >
                        Remove from club
                      </button>
                    </PinProtectedButton>
                  )}
                  {/* Delete from family: severs the relationship across ALL clubs; they stay a club member. */}
                  {canUnlinkSecondaryContact && (
                    <CustomButton
                      style={{ ...defaultButtonStyle, marginBottom: 0, height: 'fit-content', padding: '4px 12px', fontSize: '13px', backgroundColor: '#cf2c47' }}
                      onClick={() =>
                        handleDeleteClick('delete-secondary-from-family', secondaryContact)
                      }
                      title="Remove the secondary-contact relationship for this family — affects ALL clubs; they stay a club member"
                    >
                      Delete from family
                    </CustomButton>
                  )}
                </>
              ) : (
                canEditSecondaryContact && (
                  <CustomButton
                    style={{ ...defaultButtonStyle, marginBottom: 0, height: 'fit-content', padding: '4px 12px', fontSize: '13px' }}
                    onClick={handleAddSecondaryContact}
                    title="Add a secondary contact for this family"
                  >
                    + Add secondary contact
                  </CustomButton>
                )
              )}
            </div>
          )}
        </div>

        <div className={styles.cardContent}>
          {userPermissionLevel >= UserPermissionLevel.Member ? (
            secondaryContact ? (
              <>
                <div className={styles.infoGrid}>
                  <LabelText
                    label="Contact Name"
                    text={`${secondaryContact.firstName} ${secondaryContact.lastName}`}
                  />
                  <LabelText
                    label="Contact Email"
                    text={secondaryContact.email}
                  />
                </div>
                {secondaryInClub ? (
                  <div className={styles.statusRow}>
                    <LabelText label="Permissions" text={secondaryContact.role} />
                    <LabelText
                      label="Club Status"
                      text={secondaryContact.participationStatus}
                    />
                    <>
                      <LabelText
                        label="Portal Access"
                        text={secondaryContact.invitationStatus}
                      />
                      {secondaryContact.invitationStatus !== 'Accepted' && (
                        <div className={styles.inviteButtonWrapper}>
                          {renderInviteButton(secondaryContact, 'secondary')}
                        </div>
                      )}
                    </>
                  </div>
                ) : (
                  <div className={styles.statusRow}>
                    <LabelText label="Club Status" text="Not a member of this club" />
                    <LabelText
                      label="Portal Access"
                      text={secondaryContact.invitationStatus}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className={styles.emptyState}>
                <p>No secondary contact added</p>
              </div>
            )
          ) : (
            <div className={styles.emptyState}>
              <p>Not available for Guest accounts</p>
            </div>
          )}
        </div>
      </div>
      {/* Athlete Profiles Card */}
      <AthleteProfiles
        athletes={athletes}
        otherClubAthletes={otherClubAthletes}
        clubId={clubId}
        email={member?.email}
        onEditAthlete={handleEditAthlete}
        onAddAthlete={handleAddAthlete}
        onDeleteAthlete={(athlete, pin) =>
          handleDeleteClick('athlete', athlete)
        }
        onLinkAthlete={handleLinkAthlete}
        clubTeams={clubTeams}
        canEdit={canEditDeleteAthlete} // This controls edit/delete/link buttons in athlete table
        canAdd={canAddAthlete} // This will control the "create new athlete" button
      />

      {/* Modals */}
      {showMemberModal && (
        <MemberFormModal
          show={showMemberModal}
          member={member}
          clubId={clubId}
          onClose={() => setShowMemberModal(false)}
          onSuccess={() => {
            setShowMemberModal(false);
            onRefresh?.();
          }}
        />
      )}

      {showSecondaryModal && (
        <SecondaryContactModal
          show={showSecondaryModal}
          contact={secondaryContact}
          primaryContactId={member?.userID}
          primaryContactName={`${member?.firstName || ''} ${member?.lastName || ''}`.trim()}
          clubId={clubId}
          onClose={() => setShowSecondaryModal(false)}
          onSuccess={() => {
            setShowSecondaryModal(false);
            onRefresh?.();
          }}
        />
      )}

      {showAthleteModal && (
        <AthleteFormModal
          show={showAthleteModal}
          athlete={editingAthlete}
          memberEmail={member?.email}
          memberName={
            member?.firstName || member?.lastName
              ? `${member?.firstName || ''} ${member?.lastName || ''}`.trim()
              : member?.email || 'Unknown Member'
          }
          clubId={clubId}
          clubTeams={clubTeams}
          onClose={() => {
            setShowAthleteModal(false);
            setEditingAthlete(null);
          }}
          onSuccess={() => {
            setShowAthleteModal(false);
            setEditingAthlete(null);
            onRefresh?.();
          }}
        />
      )}

      {showLinkAthleteDialog && linkAthleteTarget && (
        <LinkAthleteToClubDialog
          show={showLinkAthleteDialog}
          athlete={linkAthleteTarget}
          memberName={
            member?.firstName || member?.lastName
              ? `${member?.firstName || ''} ${member?.lastName || ''}`.trim()
              : member?.email || 'Unknown Member'
          }
          memberRole={member?.role}
          clubId={clubId}
          clubTeams={clubTeams}
          onClose={() => {
            setShowLinkAthleteDialog(false);
            setLinkAthleteTarget(null);
          }}
          onSuccess={() => {
            setShowLinkAthleteDialog(false);
            setLinkAthleteTarget(null);
            onRefresh?.();
          }}
        />
      )}

      {/* Invite Confirmation Modal */}
      {showInviteModal && (
        <CustomConfirmModal
          show={showInviteModal}
          title={isResendInvite ? 'Resend Invitation' : 'Send Invitation'}
          content={
            <div>
              <p>
                {isResendInvite
                  ? `Resend invitation to `
                  : `Send invitation to `}
                <strong>{getDisplayName(inviteTarget)}</strong>?
              </p>
              <p
                style={{
                  fontSize: '0.875rem',
                  marginTop: '0.75rem',
                  color: '#6b7280',
                }}
              >
                {invitationAccessText(club)}
              </p>
            </div>
          }
          confirmLabel={
            isResendInvite ? 'Resend Invitation' : 'Send Invitation'
          }
          loading={inviting}
          onConfirm={confirmInvitation}
          onCancel={() => {
            setShowInviteModal(false);
            setInviteTarget(null);
          }}
        />
      )}

      {showDeleteModal && (
        <CustomConfirmModal
          show={showDeleteModal}
          title={
            deleteTarget?.type === 'member'
              ? 'Remove Member from Club'
              : deleteTarget?.type === 'athlete'
              ? 'Remove Athlete from Club'
              : deleteTarget?.type === 'remove-secondary-from-club'
              ? 'Remove Secondary Contact from Club'
              : deleteTarget?.type === 'delete-secondary-from-family'
              ? 'Delete Secondary Contact from Family'
              : 'Remove'
          }
          content={
            deleteTarget?.type === 'member'
              ? 'Remove this member from the club? Their portal account is preserved, along with any membership they hold in other clubs.'
              : deleteTarget?.type === 'athlete'
              ? "Remove this athlete from the club? Their race results history is preserved."
              : deleteTarget?.type === 'remove-secondary-from-club'
              ? `Remove ${getDisplayName(deleteTarget.item)} from this club? They stay the family's secondary contact (they'll show as "not in this club" and can be re-added) and keep any membership in other clubs. This club's data for them is removed.`
              : deleteTarget?.type === 'delete-secondary-from-family'
              ? `Delete ${getDisplayName(deleteTarget.item)} as this family's secondary contact? ⚠️ This removes the secondary-contact relationship for these athletes across ALL clubs — it is not limited to this club.${
                  secondaryInClub
                    ? ' They remain a member of this club (with no athletes); remove them from the club separately if you want.'
                    : ' They are not a member of this club, so this only removes the family relationship — their platform account is unaffected.'
                }`
              : 'Remove this item from the club?'
          }
          confirmLabel={
            deleteTarget?.type === 'remove-secondary-from-club'
              ? 'Remove from club'
              : deleteTarget?.type === 'delete-secondary-from-family'
              ? 'Delete from family'
              : 'Remove'
          }
          confirmClassName={styles.deleteConfirm}
          loading={deleting}
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setDeleteTarget(null);
          }}
        />
      )}

      {/* Athlete / member removal — same change-preview + cascade as the bulk lists (§5.6) */}
      <RosterRemovePreviewModal
        show={!!rosterRemove}
        clubId={clubId}
        seed={rosterRemove?.seed}
        title={rosterRemove?.title || 'Remove'}
        typeLabel={rosterRemove?.typeLabel || ''}
        onClose={() => setRosterRemove(null)}
        onApplied={handleRosterRemoveApplied}
      />
    </div>
  );
};

MemberDetails.propTypes = {
  member: PropTypes.object.isRequired,
  clubId: PropTypes.string.isRequired,
  secondaryContact: PropTypes.object,
  athletes: PropTypes.array,
  otherClubAthletes: PropTypes.array,
  onRefresh: PropTypes.func,
};

export default MemberDetails;
